import { useState } from "react";
import { useLang } from "@/i18n";
import { Lock, Loader2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { meta } from "@/analytics/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function OrderForm({ defaultStep = "valutazione", dual = false, onDone, testidPrefix = "form" }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", cf: "", topic: "", notes: "", privacy: false });
  const [db, setDb] = useState("");
  const [busy, setBusy] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (step) => {
    if (busy) return;
    setBusy(step);
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step, ...form, registries: db ? [db] : [] }),
      });
      if (!res.ok) throw new Error("order");
      const order = await res.json();
      if (step === "visure") {
        meta.initiateCheckout({ value: 99, currency: "EUR", content_ids: ["visure_99"], content_type: "product", num_items: 1 });
        const chk = await fetch(`${API}/payments/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lookup_key: "visure_99", origin_url: window.location.origin, order_id: order.id }),
        });
        if (!chk.ok) throw new Error("checkout");
        const { checkout_url } = await chk.json();
        window.location.href = checkout_url;
        return;
      }
      toast.success(step === "valutazione" ? t("form.successEval") : t("form.success2"));
      meta.lead({ content_name: step === "valutazione" ? "valutazione_gratuita" : "richiesta_cancellazione" });
      setForm({ name: "", email: "", phone: "", city: "", cf: "", topic: "", notes: "", privacy: false });
      setDb("");
      onDone?.();
    } catch {
      toast.error(t("form.err"));
    } finally {
      setBusy(null);
    }
  };

  const topicOptions = t("form.topicOptions").split("|");
  const dbOptions = t("form.dbOptions").split("|");

  const submitPayPal = async () => {
    if (busy) return;
    const formEl = document.querySelector(`[data-testid="${testidPrefix}-form"]`);
    if (formEl && !formEl.reportValidity()) return;
    setBusy("paypal");
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: "visure", ...form, registries: db ? [db] : [] }),
      });
      if (!res.ok) throw new Error("order");
      const order = await res.json();
      meta.initiateCheckout({ value: 99, currency: "EUR", content_ids: ["visure_99"], content_type: "product", num_items: 1 });
      const pp = await fetch(`${API}/paypal/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: order.id, origin_url: window.location.origin }),
      });
      if (!pp.ok) throw new Error("paypal");
      const { approve_url } = await pp.json();
      window.location.href = approve_url;
    } catch {
      toast.error(t("form.err"));
      setBusy(null);
    }
  };

  const paypalButton = PAYPAL_CLIENT_ID ? (
    <button type="button" data-testid={`${testidPrefix}-paypal-button`} disabled={!!busy} onClick={submitPayPal}
      className="w-full rounded-full px-6 py-4 text-sm font-bold flex items-center justify-center gap-2 bg-[#FFC439] text-[#002F87] hover:bg-[#F7BA20] transition-colors duration-200 disabled:opacity-60">
      {busy === "paypal" ? <Loader2 size={16} className="animate-spin" /> : null}
      Paga con <span className="italic font-extrabold">PayPal</span>
    </button>
  ) : null;

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(defaultStep); }} className="space-y-4" data-testid={`${testidPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid={`${testidPrefix}-name-input`} required value={form.name} onChange={set("name")} placeholder={t("form.name")} className="input-field" />
        <input data-testid={`${testidPrefix}-phone-input`} required value={form.phone} onChange={set("phone")} placeholder={t("form.phone")} className="input-field" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid={`${testidPrefix}-email-input`} required type="email" value={form.email} onChange={set("email")} placeholder={t("form.email")} className="input-field" />
        <input data-testid={`${testidPrefix}-city-input`} value={form.city} onChange={set("city")} placeholder={t("form.city")} className="input-field" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <select data-testid={`${testidPrefix}-topic-select`} value={form.topic} onChange={set("topic")} className="input-field text-[#1A1A1A]">
          <option value="">{t("form.topic")}</option>
          {topicOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select data-testid={`${testidPrefix}-db-select`} value={db} onChange={(e) => setDb(e.target.value)} className="input-field text-[#1A1A1A]">
          <option value="">{t("form.db")}</option>
          {dbOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      {!dual && (
        <input data-testid={`${testidPrefix}-cf-input`} value={form.cf} onChange={set("cf")} placeholder={t("form.cf")} maxLength={16} className="input-field" />
      )}
      <textarea data-testid={`${testidPrefix}-notes-input`} value={form.notes} onChange={set("notes")} placeholder={t("form.notes")} rows={dual ? 3 : 2} className="input-field" />

      <label className="flex items-start gap-3 text-xs text-[#64748B] cursor-pointer">
        <input data-testid={`${testidPrefix}-privacy-check`} type="checkbox" checked={form.privacy} onChange={set("privacy")} required className="mt-0.5 accent-[#00AEEF]" />
        {t("form.privacy")}
      </label>

      {dual ? (
        <div className="space-y-3 pt-1">
          <button type="button" data-testid="contact-form-submit-button" disabled={!!busy} onClick={() => submit("valutazione")}
            className="btn-green w-full rounded-full px-6 py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "valutazione" ? <Loader2 size={16} className="animate-spin" /> : <FileCheck2 size={16} />}
            {t("form.eval")}
          </button>
          <p className="text-center text-xs text-[#94A3B8] font-medium">{t("form.or")}</p>
          <button type="button" data-testid="contact-form-pay-button" disabled={!!busy} onClick={() => submit("visure")}
            className="btn-outline w-full rounded-full px-6 py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "visure" ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
            {t("form.pay1")}
          </button>
          <p className="text-center text-[11px] text-[#94A3B8]">{t("form.secure")}</p>
          {paypalButton}
        </div>
      ) : (
        <div className="pt-1">
          <button type="submit" data-testid={`${testidPrefix}-submit-button`} disabled={!!busy}
            className="btn-green w-full rounded-full px-6 py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
            {defaultStep === "visure" ? t("form.pay1") : t("form.pay2")}
          </button>
          {defaultStep === "visure" && (
            <p className="text-center text-[11px] text-[#94A3B8] mt-3">{t("form.secure")}</p>
          )}
          {defaultStep === "visure" && paypalButton}
        </div>
      )}
    </form>
  );
}
